import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  Loader2, Link2, Unplug, RefreshCw, AlertTriangle, CheckCircle2, Clock, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "../../App";

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const IntegrationsTab = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [bizInput, setBizInput] = useState("");
  const [linking, setLinking] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/thumbtack/status`, { headers: headers() });
      setStatus(res.data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    // Post-OAuth redirect feedback
    const params = new URLSearchParams(window.location.search);
    const result = params.get("thumbtack");
    if (result === "connected") {
      toast.success("Thumbtack connected! New leads will flow into your Jobs automatically.");
    } else if (result === "error") {
      toast.error(`Thumbtack connection failed: ${params.get("reason") || "unknown error"}`);
    }
    if (result) {
      params.delete("thumbtack");
      params.delete("reason");
      const qs = params.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
    }
  }, [fetchStatus]);

  useEffect(() => {
    if (status?.backfill?.status !== "running") return;
    const t = setTimeout(fetchStatus, 5000);
    return () => clearTimeout(t);
  }, [status, fetchStatus]);

  const connect = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/thumbtack/connect`, { headers: headers() });
      window.location.assign(res.data.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Thumbtack connection");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Thumbtack? New leads will stop syncing until you reconnect.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/thumbtack/disconnect`, {}, { headers: headers() });
      toast.success("Thumbtack disconnected");
      await fetchStatus();
    } catch {
      toast.error("Failed to disconnect");
    } finally {
      setBusy(false);
    }
  };

  const linkBusiness = async () => {
    const raw = bizInput.trim();
    if (!raw) {
      toast.error("Paste your Thumbtack business ID or service page URL first");
      return;
    }
    setLinking(true);
    try {
      const res = await axios.post(`${API}/thumbtack/link-business`, { business_id: raw }, { headers: headers() });
      const created = res.data?.created || 0;
      toast.success(`Linked ${res.data.business_name}${created ? ` — pulled ${created} lead${created === 1 ? "" : "s"}` : ""}`);
      setBizInput("");
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not link business");
    } finally {
      setLinking(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/thumbtack/sync`, {}, { headers: headers() });
      if (res.data?.error) {
        toast.error(res.data.error);
      } else {
        const created = res.data?.created || 0;
        toast.success(created ? `Synced ${created} new lead${created === 1 ? "" : "s"} from Thumbtack` : "Up to date — no new leads");
      }
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
      await fetchStatus();
    } finally {
      setSyncing(false);
    }
  };

  const retryImport = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/thumbtack/backfill/run`, {}, { headers: headers() });
      toast.success("Import restarted — already-imported leads won't be duplicated");
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the import");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const st = status || {};
  const needsReconnect = st.status === "needs_reconnection";
  const bf = st.backfill;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="integrations-tab">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Integrations</h3>
        <p className="text-sm text-muted-foreground">Connect outside lead sources to your CRM. QuickBooks and Stripe have their own tabs.</p>
      </div>

      <Card data-testid="thumbtack-card">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-[#009fd9]/15 flex items-center justify-center shrink-0">
                <span className="text-[#009fd9] font-bold text-lg">T</span>
              </div>
              <div className="min-w-0">
                <CardTitle className="text-base">Thumbtack</CardTitle>
                <CardDescription className="text-xs">
                  Pull Thumbtack leads &amp; customer messages straight into your Jobs pipeline
                </CardDescription>
              </div>
            </div>
            {st.connected ? (
              needsReconnect ? (
                <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30 gap-1" data-testid="thumbtack-status-badge">
                  <AlertTriangle className="w-3 h-3" /> Needs reconnection
                </Badge>
              ) : (
                <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 gap-1" data-testid="thumbtack-status-badge">
                  <CheckCircle2 className="w-3 h-3" /> Connected
                </Badge>
              )
            ) : (
              <Badge variant="outline" className="text-muted-foreground gap-1" data-testid="thumbtack-status-badge">
                Not connected
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!st.configured ? (
            <div className="rounded-lg border border-dashed p-4 space-y-2" data-testid="thumbtack-not-configured-note">
              <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-amber-500" /> Awaiting Thumbtack API access
              </p>
              <p className="text-xs text-muted-foreground">
                Thumbtack's Partner API is approval-gated. Request access at{" "}
                <a href="https://developers.thumbtack.com/request-access" target="_blank" rel="noreferrer" className="underline">
                  developers.thumbtack.com/request-access
                </a>
                . Once approved, add your Client ID and Secret to the app configuration and this card unlocks automatically.
              </p>
              <div className="pt-1">
                <p className="text-[11px] uppercase font-semibold tracking-wider text-muted-foreground mb-1">
                  Redirect URI to register with Thumbtack
                </p>
                <div className="flex items-center gap-2 min-w-0">
                  <code className="flex-1 min-w-0 text-xs p-2 rounded-lg truncate bg-muted text-muted-foreground" data-testid="thumbtack-redirect-uri">
                    {st.redirect_uri}
                  </code>
                  <Button
                    variant="outline" size="sm" className="h-8 px-2 shrink-0"
                    onClick={() => { navigator.clipboard.writeText(st.redirect_uri || ""); toast.success("Copied!"); }}
                    data-testid="thumbtack-copy-redirect"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ) : st.connected ? (
            <>
              {needsReconnect && (
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3 flex items-start gap-2" data-testid="thumbtack-reconnect-banner">
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-foreground">
                    Your Thumbtack authorization expired{st.last_error ? ` (${st.last_error})` : ""}. Reconnect to resume lead syncing — no leads are lost, we'll catch up on reconnect.
                  </p>
                </div>
              )}
              {!needsReconnect && !st.business_name && (
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3 space-y-2.5" data-testid="thumbtack-no-business-banner">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                    <p className="text-xs text-foreground">
                      No Thumbtack business is linked yet{st.last_error ? ` — ${st.last_error}` : ", so leads can't sync."}
                    </p>
                  </div>
                  <div className="pl-6 space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input
                        value={bizInput}
                        onChange={(e) => setBizInput(e.target.value)}
                        placeholder="Business ID or Thumbtack profile URL"
                        className="h-8 text-xs max-w-xs"
                        data-testid="thumbtack-business-id-input"
                      />
                      <Button size="sm" className="h-8 text-xs bg-[#009fd9] text-white hover:bg-[#008ec2] gap-1.5" onClick={linkBusiness} disabled={linking} data-testid="thumbtack-link-business-btn">
                        {linking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Link business
                      </Button>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Open your <span className="font-medium">public</span> Thumbtack profile (Profile → "View profile" or "Share") and paste that link — it contains <code className="bg-muted px-1 rounded">/service/</code> followed by your business number. Your logged-in dashboard URL won't work.
                    </p>
                  </div>
                </div>
              )}
              {bf?.status === "running" && (
                <div className="rounded-lg bg-[#009fd9]/10 border border-[#009fd9]/20 p-3 flex items-center gap-2" data-testid="thumbtack-backfill-running">
                  <Loader2 className="w-4 h-4 animate-spin text-[#009fd9] shrink-0" />
                  <p className="text-xs text-foreground">
                    Importing existing Thumbtack leads &amp; message history… {bf.processed || 0}/{bf.negotiations_total || 0} processed. This can take a while for busy accounts.
                  </p>
                </div>
              )}
              {(bf?.status === "incomplete" || bf?.status === "failed") && (
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3 flex items-start justify-between gap-2 flex-wrap" data-testid="thumbtack-backfill-error">
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                    <p className="text-xs text-foreground">
                      Import incomplete{bf.last_error ? ` — ${bf.last_error}` : ""}. Retrying never duplicates what was already imported.
                    </p>
                  </div>
                  <Button variant="outline" size="sm" className="h-7 text-xs shrink-0" onClick={retryImport} disabled={busy} data-testid="thumbtack-backfill-retry">
                    Retry import
                  </Button>
                </div>
              )}
              {st.messaging_write_enabled === false && !needsReconnect && (
                <div className="rounded-lg bg-blue-500/10 border border-blue-500/20 p-3 flex items-start justify-between gap-2 flex-wrap" data-testid="thumbtack-messaging-scope-note">
                  <p className="text-xs text-foreground min-w-0 flex-1">
                    Two-way messaging is new — reconnect Thumbtack once to grant the messaging permission, then reply to leads right from your jobs.
                  </p>
                  <Button variant="outline" size="sm" className="h-7 text-xs shrink-0" onClick={connect} disabled={busy} data-testid="thumbtack-grant-messaging">
                    Reconnect
                  </Button>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div className="min-w-0">
                  <p className="text-[11px] uppercase font-semibold tracking-wider text-muted-foreground">Business</p>
                  <p className="font-medium truncate" data-testid="thumbtack-business-name">{st.business_name || "—"}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase font-semibold tracking-wider text-muted-foreground">Leads synced</p>
                  <p className="font-medium" data-testid="thumbtack-leads-synced">{st.leads_synced ?? 0}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase font-semibold tracking-wider text-muted-foreground">Last sync</p>
                  <p className="font-medium" data-testid="thumbtack-last-sync">
                    {st.last_sync_at ? new Date(st.last_sync_at).toLocaleString() : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase font-semibold tracking-wider text-muted-foreground">Failed</p>
                  <p className={`font-medium ${st.leads_failed ? "text-red-500" : ""}`} data-testid="thumbtack-leads-failed">{st.leads_failed ?? 0}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {needsReconnect && (
                  <Button size="sm" onClick={connect} disabled={busy} className="bg-[#009fd9] text-white hover:bg-[#008ec2] gap-1.5" data-testid="thumbtack-reconnect-btn">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Reconnect
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={syncNow} disabled={syncing} className="gap-1.5" data-testid="thumbtack-sync-btn">
                  {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Sync now
                </Button>
                <Button size="sm" variant="ghost" onClick={disconnect} disabled={busy} className="gap-1.5 text-red-500 hover:text-red-600" data-testid="thumbtack-disconnect-btn">
                  <Unplug className="w-3.5 h-3.5" /> Disconnect
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                New Thumbtack leads arrive in real time and are double-checked every 15 minutes. Each lead creates a customer + job tagged
                <span className="font-mono"> thumbtack-lead</span> with source "Thumbtack" — customer messages append to the job's notes.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Connect your Thumbtack Pro account and every new Thumbtack lead lands in your Jobs pipeline automatically —
                customer, service, message and all — tagged with source "Thumbtack". No manual entry.
              </p>
              <div>
                <p className="text-[11px] uppercase font-semibold tracking-wider text-muted-foreground mb-1">
                  Redirect URI — must be pre-registered on your Thumbtack {st.environment === "staging" ? "staging" : "production"} client
                </p>
                <div className="flex items-center gap-2 min-w-0">
                  <code className="flex-1 min-w-0 text-xs p-2 rounded-lg truncate bg-muted text-muted-foreground" data-testid="thumbtack-redirect-uri">
                    {st.redirect_uri}
                  </code>
                  <Button
                    variant="outline" size="sm" className="h-8 px-2 shrink-0"
                    onClick={() => { navigator.clipboard.writeText(st.redirect_uri || ""); toast.success("Copied!"); }}
                    data-testid="thumbtack-copy-redirect"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Environment: <span className="font-mono" data-testid="thumbtack-environment">{st.environment || "production"}</span> — the URL above must match a pre-registered redirect URL byte-for-byte (scheme, host, no trailing slash).
                </p>
              </div>
              <Button onClick={connect} disabled={busy} className="bg-[#009fd9] text-white hover:bg-[#008ec2] gap-1.5" data-testid="thumbtack-connect-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Connect Thumbtack
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default IntegrationsTab;
